import { oc } from "@orpc/contract";
import { z } from "zod";
import { EmailDraftSummarySchema } from "./email-drafts.contract";

// ── Shared Schemas ────────────────────────────────────────────────────

export const SuggestedActionTypeSchema = z.enum([
  "adjust_line_item_price",
  "remove_line_item",
  "update_header_field",
]);

export const SuggestedActionStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
  "executed",
  "failed",
]);

export const SuggestedActionResponseSchema = z.object({
  id: z.uuid(),
  agentRunId: z.uuid(),
  entityType: z.string().nullable(),
  entityId: z.uuid().nullable(),
  actionType: SuggestedActionTypeSchema,
  description: z.string(),
  parameters: z.record(z.string(), z.unknown()),
  status: SuggestedActionStatusSchema,
  approvedBy: z.uuid().nullable(),
  approvedAt: z.string().nullable(),
  rejectedBy: z.uuid().nullable(),
  rejectedAt: z.string().nullable(),
  executedAt: z.string().nullable(),
  executionError: z.string().nullable(),
  sortOrder: z.number(),
  createdAt: z.string(),
});

export const ExecuteActionResultSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  action: SuggestedActionResponseSchema,
});

// ── Contract ──────────────────────────────────────────────────────────

export const suggestedActionContracts = {
  /**
   * List pending suggested actions for an entity.
   * Returns actions from the most recent agent run.
   */
  listByEntity: oc
    .input(
      z.object({
        entityType: z.string(),
        entityId: z.uuid(),
        conversationId: z.string().optional(),
      }),
    )
    .output(
      z.object({
        actions: z.array(SuggestedActionResponseSchema),
        emailDraft: EmailDraftSummarySchema.nullable(),
        isAgentRunning: z.boolean(),
      }),
    )
    .route({ method: "GET" }),

  /**
   * Approve and execute a suggested action in one step.
   * The action is approved, then its mutation is applied to the staged order.
   */
  approveAndExecute: oc
    .input(
      z.object({
        actionId: z.uuid(),
      }),
    )
    .output(ExecuteActionResultSchema)
    .route({ method: "POST" }),

  /**
   * Reject a suggested action.
   */
  reject: oc
    .input(
      z.object({
        actionId: z.uuid(),
      }),
    )
    .output(
      z.object({
        action: SuggestedActionResponseSchema,
      }),
    )
    .route({ method: "POST" }),
} as const;

// ── Type Exports ──────────────────────────────────────────────────────

export type SuggestedActionResponse = z.infer<
  typeof SuggestedActionResponseSchema
>;
export type ExecuteActionResult = z.infer<typeof ExecuteActionResultSchema>;
